import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { Check, Lock } from 'lucide-react';
import { Popover, useToast } from '@missions/ui';

export type MenuItem =
  | '-'
  | { header: string }
  | {
      label: string;
      icon?: ReactNode;
      dot?: string;
      onClick?: () => void;
      danger?: boolean;
      selected?: boolean;
      disabled?: boolean;
      /** role needed, shown with a lock when disabled */
      lock?: string;
      hint?: string;
    };

export interface MenuProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  title?: string;
  items: MenuItem[];
  minWidth?: number;
  alignRight?: boolean;
}

/** Designed action menu (popover on desktop, bottom sheet on phones), keyboard navigable. */
export function Menu({ anchorRef, open, onClose, title, items, minWidth = 230, alignRight }: MenuProps) {
  const toast = useToast();
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => boxRef.current?.querySelector<HTMLButtonElement>('.mx-i:not([aria-disabled])')?.focus({ preventScroll: true }), 30);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <Popover anchorRef={anchorRef} open={open} onClose={onClose} title={title} minWidth={minWidth} alignRight={alignRight} role="menu">
      <div
        className="mx"
        ref={boxRef}
        onKeyDown={(e) => {
          const bs = Array.from(boxRef.current?.querySelectorAll<HTMLButtonElement>('.mx-i') ?? []);
          const i = bs.indexOf(document.activeElement as HTMLButtonElement);
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            bs[(i + 1) % bs.length]?.focus();
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            bs[(i - 1 + bs.length) % bs.length]?.focus();
          }
        }}
      >
        {items.map((it, i) => {
          if (it === '-') return <div key={i} className="mx-sep" role="separator" />;
          if ('header' in it) return <div key={i} className="mx-h">{it.header}</div>;
          return (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`mx-i${it.danger ? ' danger' : ''}${it.selected ? ' sel' : ''}`}
              aria-disabled={it.disabled || undefined}
              onClick={() => {
                if (it.disabled) {
                  toast(it.lock ? `Only ${it.lock}s and above can do this` : 'This option isn’t available here', { type: 'info' });
                  return;
                }
                onClose();
                setTimeout(() => it.onClick?.(), 0);
              }}
            >
              {it.icon}
              {it.dot && <span className="dot" style={{ background: it.dot, borderRadius: '50%' }} />}
              <span className="mx-l">{it.label}</span>
              {it.disabled && it.lock ? (
                <span className="mx-k"><Lock />{it.lock}</span>
              ) : it.hint ? (
                <span className="mx-k">{it.hint}</span>
              ) : null}
              {it.selected && <Check className="mx-c" />}
            </button>
          );
        })}
      </div>
    </Popover>
  );
}
